import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  XIcon,
  OfficeBuildingIcon,
  MailIcon,
  PhoneIcon,
  LocationMarkerIcon,
  PhotographIcon,
  HashtagIcon,
  DocumentTextIcon,
  RefreshIcon,
} from "@heroicons/react/outline";

const EditResortModal = ({
  isOpen,
  onClose,
  selectedResortId,
  fetchResorts,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [formData, setFormData] = useState({
    resort_name: "",
    resort_code: "",
    resort_description: "",
    resort_phone: "",
    resort_email: "",
    resort_address_one: "",
    resort_address_two: "",
    resort_logo: "",
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch the existing resort data
  const fetchResortDetails = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${baseUrl}/resort/${selectedResortId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data && response.data.data) {
        const resort = response.data.data;
        setFormData({
          resort_name: resort.resort_name || "",
          resort_code: resort.resort_code || "",
          resort_description: resort.resort_description || "",
          resort_phone: resort.resort_phone || "",
          resort_email: resort.resort_email || "",
          resort_address_one: resort.resort_address_one || "",
          resort_address_two: resort.resort_address_two || "",
          resort_logo: resort.resort_logo || "",
        });
      } else {
        toast.error("Property data not found.");
      }
    } catch (error) {
      toast.error("Error fetching property details.");
      console.error("Error fetching resort details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && selectedResortId) {
      fetchResortDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedResortId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "resort_code"
        ? value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12)
        : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const accessToken = localStorage.getItem("accessToken");
      await axios.put(`${baseUrl}/resort/${selectedResortId}`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success("Property updated successfully");
      onClose();
      fetchResorts();
    } catch (error) {
      toast.error("Failed to update property");
      console.error("Failed to update resort:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="relative w-full max-w-2xl transform transition-all">
            <div className="modal-container">
              {/* Header */}
              <div className="modal-header">
                <div className="flex items-center space-x-3">
                  <div className="modal-icon">
                    <OfficeBuildingIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="modal-title">Edit Property</h3>
                    <p className="modal-subtitle">Update property information</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="modal-close-btn"
                  type="button"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Loading State */}
              {loading ? (
                <div className="loading-container">
                  <RefreshIcon className="loading-spinner" />
                  <p className="loading-text">Loading property details...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {/* Grid Layout for Form Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Property Name */}
                      <div className="form-group md:col-span-2">
                        <label htmlFor="resort_name" className="form-label">
                          <OfficeBuildingIcon className="form-label-icon" />
                          Property Name *
                        </label>
                        <input
                          type="text"
                          id="resort_name"
                          name="resort_name"
                          value={formData.resort_name}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="e.g., Sunset Resort & Spa"
                          required
                        />
                      </div>

                      {/* Property Code */}
                      <div className="form-group">
                        <label htmlFor="resort_code" className="form-label">
                          <HashtagIcon className="form-label-icon" />
                          Property Code *
                        </label>
                        <input
                          type="text"
                          id="resort_code"
                          name="resort_code"
                          value={formData.resort_code}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="e.g., BONSTINO or 100001"
                          pattern="[A-Z0-9]{2,12}"
                          maxLength={12}
                          autoCapitalize="characters"
                          spellCheck="false"
                          required
                        />
                        <p className="form-helper-text">
                          Use 2-12 uppercase letters and numbers only.
                        </p>
                      </div>

                      {/* Phone */}
                      <div className="form-group">
                        <label htmlFor="resort_phone" className="form-label">
                          <PhoneIcon className="form-label-icon" />
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="resort_phone"
                          name="resort_phone"
                          value={formData.resort_phone}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="e.g., +1 234 567 8900"
                        />
                      </div>

                      {/* Email */}
                      <div className="form-group md:col-span-2">
                        <label htmlFor="resort_email" className="form-label">
                          <MailIcon className="form-label-icon" />
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="resort_email"
                          name="resort_email"
                          value={formData.resort_email}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="e.g., contact@property.com"
                        />
                      </div>

                      {/* Description */}
                      <div className="form-group md:col-span-2">
                        <label htmlFor="resort_description" className="form-label">
                          <DocumentTextIcon className="form-label-icon" />
                          Property Description
                        </label>
                        <textarea
                          id="resort_description"
                          name="resort_description"
                          value={formData.resort_description}
                          onChange={handleChange}
                          className="form-textarea"
                          placeholder="Describe your property..."
                          rows="4"
                        />
                      </div>

                      {/* Address Line 1 */}
                      <div className="form-group md:col-span-2">
                        <label htmlFor="resort_address_one" className="form-label">
                          <LocationMarkerIcon className="form-label-icon" />
                          Address Line 1
                        </label>
                        <input
                          type="text"
                          id="resort_address_one"
                          name="resort_address_one"
                          value={formData.resort_address_one}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Street address, P.O. box"
                        />
                      </div>

                      {/* Address Line 2 */}
                      <div className="form-group md:col-span-2">
                        <label htmlFor="resort_address_two" className="form-label">
                          <LocationMarkerIcon className="form-label-icon" />
                          Address Line 2
                        </label>
                        <input
                          type="text"
                          id="resort_address_two"
                          name="resort_address_two"
                          value={formData.resort_address_two}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Apartment, suite, unit, building, floor, etc."
                        />
                      </div>

                      {/* Logo URL */}
                      <div className="form-group md:col-span-2">
                        <label htmlFor="resort_logo" className="form-label">
                          <PhotographIcon className="form-label-icon" />
                          Logo URL
                        </label>
                        <input
                          type="url"
                          id="resort_logo"
                          name="resort_logo"
                          value={formData.resort_logo}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="https://example.com/logo.png"
                        />
                        {formData.resort_logo && (
                          <div className="logo-preview">
                            <img 
                              src={formData.resort_logo} 
                              alt="Property logo preview"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="modal-footer">
                    <button
                      type="button"
                      onClick={onClose}
                      className="btn-secondary"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Updating...
                        </>
                      ) : (
                        'Update Property'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .modal-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #f59e0b, #f97316);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .loading-container {
          padding: 4rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .loading-spinner {
          width: 3rem;
          height: 3rem;
          color: #8b5cf6;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .loading-text {
          margin-top: 1rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .modal-body {
          padding: 2rem;
          max-height: 60vh;
          overflow-y: auto;
        }

        .modal-body::-webkit-scrollbar {
          width: 8px;
        }

        .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb {
          background: #f59e0b;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb:hover {
          background: #d97706;
        }

        .form-group {
          margin-bottom: 0;
        }

        .form-label {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .form-label-icon {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
          color: #f59e0b;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #f59e0b;
          background: white;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .logo-preview {
          margin-top: 0.75rem;
          padding: 1rem;
          background: #f9fafb;
          border: 2px dashed #e5e7eb;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-preview img {
          max-width: 200px;
          max-height: 100px;
          object-fit: contain;
          border-radius: 0.5rem;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .btn-secondary {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .btn-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #f59e0b, #f97316);
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #d97706, #ea580c);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(245, 158, 11, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default EditResortModal;
